import { useMemo, useState } from 'react'
import { usePage } from '@inertiajs/react'
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  Checkbox,
  FileButton,
  Group,
  Image,
  Loader,
  LoadingOverlay,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core'
import {
  IconAlertTriangle, IconCrop, IconFocusCentered, IconPhotoPlus, IconTrash, IconUpload,
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import StockPhotoTab from '../../Partials/Studio/AdBuilder/Form/StockPhotoTab'
import { ImageCropDialog } from '../components/assets/ImageCropDialog'
import { useImageMediaField } from './useImageMediaField'
import { proxiedImageUrl, useMediaLibraryQuery } from './useAdvertiserLibrary'
import { FocusPointPicker } from '../components/forms/FocusPointPicker'
import { approvedLibraryImages, mergeLibraryImages } from '../utils/aiSuggestions'
import classes from './ImagePickerField.module.css'

/**
 * The Figma slide image control: a preview panel on the left, and on the right a source
 * switcher over a checkbox thumbnail grid.
 *
 * "Advertiser Library" has two sources. First come the images the AI run's asset
 * assessment approved: they were judged against this advertiser and this format, and
 * they are already published to durable storage. Behind them come the photographs
 * harvested live from the advertiser's own website, which is whatever the site happens
 * to carry — the site is taken from the ad's clickout URL. A URL in both is shown once.
 *
 * The approved set is read from the run's `suggestions`, never from the slides, so the
 * library holds every image the run approved and not only the few the ad used.
 *
 * "Stock Images" reuses the existing Unsplash tab from the legacy AdBuilder, which
 * already handles search, attribution and the download ping Unsplash's terms require.
 */
export function ImagePickerField(props) {
  const { t } = useTranslation()
  const { field, error, configuration } = props
  const {
    media, target, accept, sourceUrl, previewUrl, cropMode, cropUseful, imageWarning,
    localError, busy, cropOpened, setCropOpened, chooseFile, adoptUrl, applyCrop, clear,
    focalPoint, focusUseful, setFocalPoint,
  } = useImageMediaField(props)

  const unsplashKey = usePage().props?.unsplashKey
  const [tab, setTab] = useState('library')
  const [focusOpened, setFocusOpened] = useState(false)
  const [stockQuery, setStockQuery] = useState('')

  const website = configuration?.ad?.clickoutUrl || configuration?.website || ''
  const wantLogos = (field.ui && field.ui.librarySource) === 'logos'

  const approved = useMemo(
    () => approvedLibraryImages(configuration, props.assetSourceUrls, { logos: wantLogos }),
    [configuration, props.assetSourceUrls, wantLogos],
  )

  // AI suggestions update locally; only the website/source or an explicit reload
  // should fetch the website library. Keep its thumbnails mounted during refreshes.
  const libraryQuery = useMediaLibraryQuery({ website, logos: wantLogos, enabled: tab === 'library' })
  const libraryImages = mergeLibraryImages(approved, libraryQuery.data || [])
  const libraryLoading = Boolean(website) && (libraryQuery.isPending || libraryQuery.isFetching)
  let libraryMessage = t('editorV2.form.image.noImagesWebsite')
  if (!website) libraryMessage = t('editorV2.form.image.addClickoutUrl')
  else if (libraryQuery.isError) libraryMessage = libraryQuery.error.message

  return (
    <Stack gap={6}>
      <Text size="sm" fw={500}>{field.label}</Text>

      <div className={classes.split}>
        {/* -------- left: the current image -------- */}
        <Paper withBorder radius="md" p="sm" className={classes.previewPane}>
          <LoadingOverlay visible={busy} zIndex={5} overlayProps={{ blur: 1 }} />
          {media.sourceAssetId ? (
            <Stack gap="xs" h="100%">
              {focusOpened && sourceUrl ? (
                <FocusPointPicker
                  src={sourceUrl}
                  focalPoint={focalPoint}
                  onPick={setFocalPoint}
                />
              ) : (
                <div className={classes.previewBox}>
                  <Image src={previewUrl || sourceUrl} alt="" fit="contain" h="100%" />
                </div>
              )}
              <Group gap={6} justify="space-between">
                <Group gap={6}>
                  <FileButton accept={accept} onChange={chooseFile}>
                    {(buttonProps) => (
                      <Button {...buttonProps} size="compact-xs" variant="default" leftSection={<IconUpload size={13} />}>
                        {t('editorV2.form.common.replace')}
                      </Button>
                    )}
                  </FileButton>
                  {cropUseful && sourceUrl && (
                    <Button
                      size="compact-xs"
                      variant="default"
                      leftSection={<IconCrop size={13} />}
                      onClick={() => { setFocusOpened(false); setCropOpened(true) }}
                    >
                      {t('editorV2.form.common.crop')}
                    </Button>
                  )}
                  {focusUseful && sourceUrl && (
                    <Button
                      size="compact-xs"
                      variant={focusOpened ? 'filled' : 'default'}
                      leftSection={<IconFocusCentered size={13} />}
                      onClick={() => setFocusOpened((open) => !open)}
                    >
                      {focusOpened ? t('editorV2.form.common.done') : t('editorV2.form.common.focus')}
                    </Button>
                  )}
                </Group>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  aria-label={t('editorV2.form.common.removeLabel', { label: field.label })}
                  onClick={clear}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Stack>
          ) : (
            <FileButton accept={accept} onChange={chooseFile}>
              {(buttonProps) => (
                <button {...buttonProps} type="button" className={classes.dropTarget}>
                  <IconPhotoPlus size={22} />
                  <span>{t('editorV2.form.common.dropLine')}<br />{t('editorV2.form.common.dropFile')}</span>
                </button>
              )}
            </FileButton>
          )}
        </Paper>

        {/* -------- right: sources -------- */}
        <Box miw={0}>
          <div className={classes.tabs} role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'library'}
              className={classes.tab}
              data-active={tab === 'library' ? 'true' : 'false'}
              onClick={() => setTab('library')}
            >
              {t('editorV2.form.common.advertiserLibrary')}
            </button>
            {/* Stock photography is never a brand logo, so the tab is hidden in logo mode. */}
            {!wantLogos && (
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'stock'}
                className={classes.tab}
                data-active={tab === 'stock' ? 'true' : 'false'}
                onClick={() => setTab('stock')}
              >
                {t('editorV2.form.common.stockImages')}
              </button>
            )}
          </div>

          {wantLogos && (
            <Button
              mt="sm"
              fullWidth
              size="sm"
              radius="md"
              loading={libraryLoading}
              disabled={!website}
              onClick={() => { if (tab === 'library') libraryQuery.refetch() }}
            >
              {t('editorV2.form.brand.importLogos')}
            </Button>
          )}

          {tab === 'library' && (
            <Box mt="sm">
              {libraryLoading && libraryImages.length === 0 && (
                <Center h={160}><Loader size="sm" color="pink" /></Center>
              )}
              {!libraryLoading && libraryImages.length === 0 && (
                <Text size="xs" c="dimmed" py="lg" ta="center">{libraryMessage}</Text>
              )}
              {libraryImages.length > 0 && (
                <ScrollArea.Autosize mah={330} type="auto">
                  <SimpleGrid cols={3} spacing="sm" pr={6}>
                    {libraryImages.map((item) => (
                      <button
                        key={item.url}
                        type="button"
                        className={classes.tile}
                        onClick={() => adoptUrl(item.url, undefined, item)}
                        title={item.url}
                      >
                        <Checkbox
                          checked={false}
                          readOnly
                          size="xs"
                          className={classes.tileCheck}
                          tabIndex={-1}
                          aria-hidden
                        />
                        <img
                          className={classes.tileImage}
                          src={proxiedImageUrl(item)}
                          alt=""
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </SimpleGrid>
                </ScrollArea.Autosize>
              )}
            </Box>
          )}

          {tab === 'stock' && (
            <Box mt="sm">
              {unsplashKey ? (
                <StockPhotoTab
                  unsplashKey={unsplashKey}
                  activeValue=""
                  query={stockQuery}
                  onQueryChange={setStockQuery}
                  lang={configuration?.ad?.language}
                  onSelect={(photo) => {
                    const url = photo?.urls?.raw || photo?.urls?.full
                      || photo?.urls?.regular || photo?.urls?.small
                    adoptUrl(url, `unsplash-${photo?.id || 'photo'}.jpg`)
                  }}
                />
              ) : (
                <Text size="xs" c="dimmed" py="lg" ta="center">
                  {t('editorV2.form.common.unsplashMissing')}
                </Text>
              )}
            </Box>
          )}
        </Box>
      </div>

      {field.help && <Text size="xs" c="dimmed">{field.help}</Text>}
      {imageWarning && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} py={6}>
          <Text size="xs">{imageWarning}</Text>
        </Alert>
      )}
      {(localError || error) && <Text size="xs" c="red">{localError || error}</Text>}

      {cropMode && target && (
        <ImageCropDialog
          opened={cropOpened}
          onClose={() => setCropOpened(false)}
          onApply={applyCrop}
          sourceUrl={sourceUrl}
          crop={media.crop}
          target={target}
          label={field.label}
        />
      )}
    </Stack>
  )
}
